// http.js
import axios from 'axios';

const API_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:9001/api';

// Create an Axios instance for Bearer token authentication
const http = axios.create({
    baseURL: API_URL,
    timeout: 10000,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
    },
    withCredentials: false, // Using Bearer tokens, not cookies
});

// ============================================================================
// JSON CASING TRANSFORMATION LAYER
// ============================================================================
// Per CLAUDE.md, both frontend and backend use snake_case.
// This transformation layer is implemented for:
// 1. Explicit contract enforcement between frontend and backend
// 2. Future-proofing if we migrate frontend to camelCase (Vue convention)
// 3. Clear separation of concerns - API layer handles casing
//
// Currently: snake_case → snake_case (no-op, but explicit)
// Future: Can switch to camelCase in frontend with minimal changes
// ============================================================================

/**
 * Recursively transforms object keys to snake_case
 * Currently a no-op since both sides use snake_case,
 * but makes the contract explicit for future changes
 */
const toSnakeCase = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(toSnakeCase);

    // Currently no transformation needed (frontend already uses snake_case)
    // Future: Replace with actual snake_case conversion if moving to camelCase
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = toSnakeCase(obj[key]);
        return acc;
    }, {});
};

/**
 * Recursively transforms object keys from snake_case (backend) to frontend format
 * Currently a no-op since both sides use snake_case
 */
const fromSnakeCase = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(fromSnakeCase);

    // Currently no transformation needed (both use snake_case)
    // Future: Replace with camelCase conversion if migrating frontend
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = fromSnakeCase(obj[key]);
        return acc;
    }, {});
};

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

http.interceptors.request.use(
    async config => {
        // Add Bearer token for authentication
        const token = localStorage.getItem('admin_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // IMPORTANT: Handle FormData specially (file uploads)
        if (config.data instanceof FormData) {
            // Delete Content-Type header to let browser set it with proper boundary
            delete config.headers['Content-Type'];
        } else {
            // Transform request data to snake_case (currently no-op)
            config.data = toSnakeCase(config.data);
        }

        if (config.params) {
            config.params = toSnakeCase(config.params);
        }

        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

http.interceptors.response.use(
    response => {
        // Transform response data from backend snake_case to frontend format (currently no-op)
        if (response.data) {
            response.data = fromSnakeCase(response.data);
        }
        return response;
    },
    error => {
        // Transform error response data as well
        if (error.response?.data) {
            error.response.data = fromSnakeCase(error.response.data);
        }

        // Enhanced error handling
        if (error.response) {
            if (error.response.status === 401) {
                // Handle unauthorized access
                console.warn('⚠️ Unauthorized (401): User may need to log in again');
            } else if (error.response.status === 422) {
                // Handle validation errors
                console.warn('⚠️ Validation Error (422):', error.response.data);
            } else if (error.response.status === 403) {
                // Handle forbidden access - log but don't redirect
                // Components will handle 403 errors individually
                console.warn('⚠️ Forbidden (403): User does not have permission');
            } else if (error.response.status >= 500) {
                console.error('🚨 Server Error (5xx):', error.response.status);
            }
        } else if (error.request) {
            console.error('🚨 Network Error: No response received from server');
        }

        return Promise.reject(error);
    }
);

export default http;
